import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Bug, Loader2, Send, CheckCircle } from "lucide-react";

const DISCORD_WEBHOOK_URL = "https://discord.com/api/webhooks/1479623850592567327/mpcqXn6VIWPEsB8v10MBpK6NRpyH4gjPjGXI9U9nY596LB_aTAUtlYKriqFc2HEt2j5t";

const BugReportModal = ({ open, onOpenChange, user }) => {
  const [inGameNick, setInGameNick] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!inGameNick.trim()) {
      toast.error("Please enter your in-game nickname");
      return;
    }
    
    if (!message.trim()) {
      toast.error("Please describe the bug");
      return;
    }

    setSubmitting(true);
    
    const timestamp = new Date().toISOString();
    const appUsername = user ? user.username : "Guest (Not logged in)";
    
    const embed = {
      title: "🐛 Bug Report",
      color: 0xEF4444, // Red
      fields: [
        {
          name: "App Username",
          value: appUsername,
          inline: true
        },
        {
          name: "In-Game Nick",
          value: inGameNick,
          inline: true
        },
        {
          name: "Bug Description",
          value: message.substring(0, 1024), // Discord limit
          inline: false
        }
      ],
      footer: {
        text: "OreStore Boss Tracker - Bug Report"
      },
      timestamp: timestamp
    };

    try {
      await axios.post(DISCORD_WEBHOOK_URL, {
        embeds: [embed]
      });
      
      setSubmitted(true);
      toast.success("Bug report submitted! Thank you for your feedback.");
      
      // Reset after 2 seconds and close
      setTimeout(() => {
        setInGameNick("");
        setMessage("");
        setSubmitted(false);
        onOpenChange(false);
      }, 2000);
    } catch (error) {
      console.error("Failed to submit bug report:", error);
      toast.error("Failed to submit bug report. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setInGameNick("");
      setMessage("");
      setSubmitted(false);
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="mx-auto w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center mb-2">
            <Bug className="w-6 h-6 text-red-400" />
          </div>
          <DialogTitle>Report a Bug</DialogTitle>
          <DialogDescription>
            Found something not working correctly? Let us know!
          </DialogDescription>
        </DialogHeader>

        {submitted ? (
          <div className="text-center py-8">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-green-500">Thank You!</h3>
            <p className="text-muted-foreground mt-2">
              Your bug report has been submitted successfully.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* User Info Display */}
            <div className="p-3 bg-secondary/50 rounded-lg">
              <p className="text-xs text-muted-foreground">Submitting as:</p>
              <p className="font-medium">
                {user ? user.username : "Guest (Not logged in)"}
              </p>
            </div>

            {/* In-Game Nick */}
            <div className="space-y-2">
              <Label htmlFor="inGameNick">In-Game Nick (for contact) *</Label>
              <Input
                id="inGameNick"
                placeholder="Your character name in game"
                value={inGameNick}
                onChange={(e) => setInGameNick(e.target.value)}
                maxLength={50}
                required
              />
              <p className="text-xs text-muted-foreground">
                So we can contact you in-game if we need more info
              </p>
            </div>

            {/* Bug Description */}
            <div className="space-y-2">
              <Label htmlFor="message">Bug Description *</Label>
              <Textarea
                id="message"
                placeholder="Describe what happened, what you expected, and steps to reproduce..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={4}
                maxLength={1000}
                required
              />
              <p className="text-xs text-muted-foreground text-right">
                {message.length}/1000
              </p>
            </div>

            {/* Submit Button */}
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={handleClose}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-red-500 hover:bg-red-600"
                disabled={submitting || !inGameNick.trim() || !message.trim()}
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Submit Report
                  </>
                )}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BugReportModal;
